// frontend/src/lib/global-error-handlers.js
//
// Registers window-level listeners for unhandled promise rejections and
// uncaught errors — the things React's ErrorBoundary can't see. Forwards
// to Sentry when a capture function is available and always logs to the
// console so dev mode still shouts about them.

/**
 * Coerce an arbitrary rejection reason into an Error instance so Sentry
 * gets a proper stack when one is available.
 */
function toError(reason) {
  if (reason instanceof Error) return reason
  try {
    return new Error(typeof reason === 'string' ? reason : JSON.stringify(reason))
  } catch {
    return new Error(String(reason))
  }
}

/**
 * Install the handlers. Idempotent: calling twice re-uses the same
 * references, avoiding duplicate Sentry reports in hot-reload scenarios.
 *
 * @param {object} opts
 * @param {object} [opts.Sentry] — the @sentry/react namespace (optional; falls back to window.Sentry)
 * @param {Window} [opts.target=window]
 * @returns {() => void} teardown — removes the listeners
 */
export function installGlobalErrorHandlers({ Sentry, target } = {}) {
  const win = target || (typeof window !== 'undefined' ? window : null)
  if (!win) return () => {}

  const capture = (err, tag) => {
    const sentry = Sentry || win.Sentry
    if (sentry && typeof sentry.captureException === 'function') {
      try {
        sentry.captureException(err, { tags: { source: tag } })
      } catch (e) {
        // Never let a Sentry failure mask the original error
        // eslint-disable-next-line no-console
        console.error('[global-error-handlers] sentry capture failed', e)
      }
    }
  }

  // Re-entrancy guard: if we've already installed on this target,
  // tear the old pair down before adding fresh ones. Makes the function
  // safe to call from HMR or double-imported main.jsx test scenarios.
  if (win.__stagewiseGlobalErrorHandlers) {
    win.__stagewiseGlobalErrorHandlers()
  }

  const onRejection = (event) => {
    const err = toError(event && event.reason)
    capture(err, 'unhandledrejection')
    // eslint-disable-next-line no-console
    console.error('[unhandledrejection]', err)
  }

  const onError = (event) => {
    // event.error is populated for real Errors; fall back to message when
    // a cross-origin script fires a null-error.
    const err = event && event.error ? event.error : toError(event && event.message)
    capture(err, 'window.error')
    // eslint-disable-next-line no-console
    console.error('[window.error]', err)
  }

  win.addEventListener('unhandledrejection', onRejection)
  win.addEventListener('error', onError)

  const teardown = () => {
    win.removeEventListener('unhandledrejection', onRejection)
    win.removeEventListener('error', onError)
    delete win.__stagewiseGlobalErrorHandlers
  }

  win.__stagewiseGlobalErrorHandlers = teardown
  return teardown
}

// Exposed for tests.
export { toError }
